import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, BedDouble, BookOpen, Compass, MapPin, Package, Search, X } from 'lucide-react';
import { useLang } from '../i18n';
import { useCatalog } from '../data/catalog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  destination: { icon: MapPin, label: 'search.type.destination' },
  experience: { icon: Compass, label: 'search.type.experience' },
  stay: { icon: BedDouble, label: 'search.type.stay' },
  journey: { icon: Package, label: 'search.type.journey' },
  guide: { icon: BookOpen, label: 'search.type.guide' },
};

// Accent-insensitive search: "principe" finds "Príncipe"
const fold = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export const SearchOverlay = ({ open, onClose }) => {
  const { t, lang } = useLang();
  const { experiences, packages, destinations } = useCatalog();
  const [q, setQ] = useState('');
  const [stays, setStays] = useState([]);
  const [guides, setGuides] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    setQ('');
    setTimeout(() => inputRef.current?.focus(), 60);
  }, [open]);

  useEffect(() => {
    if (!open || stays.length || guides.length) return;
    // one merged update — two sources must never overwrite each other
    Promise.all([
      fetch(`${API}/accommodations`).then((r) => (r.ok ? r.json() : [])).catch(() => []),
      fetch(`${API}/partners?type=accommodation`).then((r) => (r.ok ? r.json() : [])).catch(() => []),
      fetch(`${API}/guides`).then((r) => (r.ok ? r.json() : [])).catch(() => []),
    ]).then(([accs, partnerStays, gs]) => {
      setStays([
        ...(Array.isArray(accs) ? accs : []),
        ...(Array.isArray(partnerStays) ? partnerStays : []).map((p) => ({ ...p, _partner: true })),
      ]);
      setGuides(Array.isArray(gs) ? gs : []);
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Lock background scroll while the overlay is open (native + Lenis smooth scroll)
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.dispatchEvent(new CustomEvent('eco:lenis-stop'));
    return () => {
      document.body.style.overflow = prev;
      window.dispatchEvent(new CustomEvent('eco:lenis-start'));
    };
  }, [open]);

  const results = useMemo(() => {
    const needle = fold(q.trim());
    if (needle.length < 2) return [];
    const hits = [];
    destinations.forEach((d) => {
      if (fold(d.name).includes(needle)) hits.push({ type: 'destination', name: d.name, sub: t('search.type.destination'), to: `/destinations/${d.slug}`, key: `d-${d.slug}` });
      (d.subs || []).forEach((s) => {
        if (fold(s.name).includes(needle)) hits.push({ type: 'destination', name: s.name, sub: t('search.type.destination'), to: `/destinations/${d.slug}/${s.slug}`, key: `s-${s.slug}` });
      });
    });
    experiences.forEach((e) => {
      if (fold(e.name).includes(needle) || fold(e.island).includes(needle)) hits.push({ type: 'experience', name: e.name, sub: `${t('search.type.experience')} · ${e.island}`, to: `/experiences/${e.slug}`, key: `e-${e.slug}` });
    });
    stays.forEach((s) => {
      if (fold(s.name).includes(needle)) hits.push({ type: 'stay', name: s.name, sub: `${t('search.type.stay')} · ${s.island || ''}`, to: s._partner ? `/partners/${s.id}` : `/accommodations/${s.slug}`, key: `st-${s.id}` });
    });
    packages.forEach((p) => {
      if (fold(p.name).includes(needle) || fold(p.destination).includes(needle)) hits.push({ type: 'journey', name: p.name, sub: `${t('search.type.journey')} · ${p.destination}`, to: `/journeys/${p.slug}`, key: `j-${p.slug}` });
    });
    guides.forEach((g) => {
      if (fold(g.title).includes(needle) || fold(g.destination).includes(needle)) hits.push({ type: 'guide', name: g.title, sub: `${t('search.type.guide')} · ${g.destination || ''}`, to: `/travel-guides/${g.slug}`, key: `g-${g.slug}` });
    });
    return hits.slice(0, 12);
  }, [q, destinations, experiences, stays, packages, guides, t]);

  if (!open) return null;

  return createPortal(
    <div data-testid="search-overlay" className="fixed inset-0 z-[95] bg-eco-deep/60 backdrop-blur-sm" onClick={onClose}>
      <div className="mx-auto mt-20 w-[min(92vw,36rem)] rounded-2xl bg-white p-5 shadow-2xl md:mt-28" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 border-b border-eco-ink/10 pb-4">
          <Search size={18} className="shrink-0 text-eco-green" />
          <input
            ref={inputRef}
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && results[0]) { navigate(results[0].to); onClose(); } }}
            placeholder={t('search.placeholder')}
            className="w-full bg-transparent text-base text-eco-ink outline-none placeholder:text-eco-ink/35"
          />
          <button data-testid="search-close" onClick={onClose} className="text-eco-ink/40 hover:text-eco-ink" aria-label={t('a.close')}><X size={18} /></button>
        </div>
        <div className="max-h-[55vh] overflow-y-auto pt-3">
          {q.trim().length >= 2 && results.length === 0 && (
            <p data-testid="search-empty" className="py-6 text-center text-sm text-eco-ink/50">{t('search.empty')}</p>
          )}
          {q.trim().length < 2 && (
            <p data-testid="search-hint" className="py-6 text-center text-sm text-eco-ink/40">{t('search.hint')}</p>
          )}
          <ul className="space-y-1">
            {results.map((r) => {
              const Icon = TYPE_META[r.type].icon;
              return (
                <li key={r.key}>
                  <Link
                    to={r.to}
                    onClick={onClose}
                    data-testid={`search-result-${r.key}`}
                    className="flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-eco-cream"
                  >
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-eco-cream text-eco-green"><Icon size={15} /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-eco-ink">{r.name}</span>
                      <span className="block text-[11px] font-semibold uppercase tracking-[0.1em] text-eco-ink/45">{r.sub}</span>
                    </span>
                    <ArrowRight size={14} className="shrink-0 text-eco-ink/25" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>,
    document.body
  );
};
